import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { getCoreRowModel } from '@tanstack/react-table';

// MUI Theme
import { Button, Stack, Tab } from '@mui/material';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';

// Utils
import { Storage } from '../../../utils/Storage';
import { getDataType, convertDataType } from '../../../utils';
import { makeSettingListColumn } from '../../../utils/columns/makeColumn';

// Services
import AuthService from '../../../services/auth.service';
import { getAllSettings, addSetting, updateSetting, deleteSetting } from '../../../services/setting.service';

// Hooks
import useConfirmDialog from '../../../hooks/useConfirmDialog';
import { useNotifyMessage } from '../../../hooks/useNotifyMessage';

// Types
import { Setting } from '../../../types/Settings';
import { AppPermissions } from '../../../constants/Permissions';
import { CONFIRM_DIALOG_CANCEL, SearchBarFilterTypes } from '../../../constants/Constants';

// Common Components
import withForm from '../../common/withForm';
import SearchBar from '../../common/SearchBar/SearchBar';
import CustomModal from '../../common/withModal';
import { AuthGuard } from '../../common/AuthGuard';
import { useStandardTable as useTable, Table } from '../../common/table/index';

import settingForm, { SettingInput } from './settingForm';
import DeviceTypeSettings from './components/DeviceTypeSettings';
import CompanySettings from './components/CompanySettings';
import { tablePrimaryButtonSx } from '../../../styles/tableButtonStyles';

// Styles
import '../../../styles/muiTabs.scss';

const SettingForm = withForm(settingForm);

interface SettingsProp {
  accessKey?: string;
}

export default function Settings(props: SettingsProp): JSX.Element {
  const { notifyMessage } = useNotifyMessage();

  const [datas, setData] = useState<any>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('1');
  const [paramFilter, setParamFilter] = useState<any>({});

  const forceReload = () => setParamFilter({ ...paramFilter, changedAt: new Date() });

  const { renderDialog, confirm } = useConfirmDialog(CONFIRM_DIALOG_CANCEL);

  const intialDefaultFilters = () => {
    const filterState = Storage.getSearchBarFiltering(SearchBarFilterTypes.SETTINGS);
    if (filterState) {
      setParamFilter({
        filters: filterState.filters,
      });
    }
  };

  useEffect(() => {
    intialDefaultFilters();
  }, []);

  useEffect(() => {
    getAllSettings({}).then((res: any) => {
      if (res.status === 200) {
        const filteredData = res.data.filter((item) => {
          for (const key in paramFilter.filters) {
            const filter = paramFilter.filters[key];
            const propName = Object.keys(filter)[0];
            return item[propName].toLowerCase().indexOf(filter[propName].toLowerCase()) >= 0;
          }
          return true;
        });

        setData(filteredData);
      }
    });
  }, [paramFilter]);

  const formik = useFormik<SettingInput>({
    initialValues: {
      id: '',
      name: '',
      dataType: 'string',
      scope: '',
      value: '',
    },
    validationSchema: () => {
      return Yup.object<SettingInput>({
        name: Yup.string().required('This field is required+'),
        scope: Yup.string().required('This field is required'),
        dataType: Yup.string().required('This field is required'),
        value: Yup.string().required('This field is required'),
      });
    },
    enableReinitialize: true,
    onSubmit: async (values: SettingInput) => {
      const { id, name, scope, value, dataType } = values;
      const payload = {
        id,
        name,
        scope,
        value: convertDataType(value, dataType),
      } as Setting;

      if (id) {
        await updateSetting(payload)
          .then(() => {
            notifyMessage('Update setting success!', 'success');
            setIsOpen(false);
            forceReload();
          })
          .catch(() => {
            notifyMessage('Unable to update setting', 'error');
          });
      } else {
        await addSetting(payload)
          .then(() => {
            notifyMessage('Create setting success!', 'success');
            setIsOpen(false);
            forceReload();
          })
          .catch(() => {
            notifyMessage('Unable to create setting', 'error');
          });
      }
    },
  });

  const handleEdit = (setting: Setting) => {
    const rawValue = setting.value;

    formik.setValues({
      id: setting.id,
      name: setting.name,
      scope: setting.scope,
      dataType: getDataType(rawValue),
      value: typeof rawValue === 'object' && rawValue !== null ? JSON.stringify(rawValue, null, 2) : rawValue,
    });

    setIsOpen(true);
  };

  const handleDelete = useCallback(
    async (id: string) => {
      const { accepted } = await confirm({});
      if (!accepted) return;
      await deleteSetting(id)
        .then(() => {
          notifyMessage('Setting has been removed', 'success');
          forceReload();
        })
        .catch(() => {
          notifyMessage('Unable to delete setting', 'error');
        });
    },
    [confirm],
  );

  const handleTabChange = useCallback((event: React.SyntheticEvent, newValue: string) => setActiveTab(newValue), []);

  const columns = useMemo(() => makeSettingListColumn(handleDelete, handleEdit), []);
  const table = useTable<any>({
    columns,
    accessKey: props.accessKey,
    serverSide: false,
  });

  return (
    <>
      <TabContext value={activeTab}>
        <div className='app-tabs'>
          <TabList onChange={handleTabChange} aria-label='lab API tabs example'>
            {AuthService.hasPermission(AppPermissions.Setting.Manage) && <Tab label='Settings' value='1' sx={{ textTransform: 'none' }} />}
            {AuthService.hasPermission(AppPermissions.Log.Reader.Read) && <Tab label='Device Type' value='2' sx={{ textTransform: 'none' }} />}
            {AuthService.hasPermission(AppPermissions.Setting.Manage) && <Tab label='Companies' value='3' sx={{ textTransform: 'none' }} />}
          </TabList>
        </div>
        <TabPanel value='1' style={{ display: 'contents' }}>
          <AuthGuard permission={AppPermissions.Setting.Manage}>
            {renderDialog()}
            <Stack direction='row' spacing={1} justifyContent='flex-end'>
              <Button
                onClick={() => {
                  formik.resetForm();
                  setIsOpen(true);
                }}
                variant='contained'
                size='small'
                endIcon={<AddCircleOutlineOutlinedIcon style={{ fontSize: 20 }} />}
                sx={{ ...tablePrimaryButtonSx, mb: '5px' }}
              >
                Add Setting
              </Button>
            </Stack>
          </AuthGuard>

          <AuthGuard permission={AppPermissions.Setting.Manage}>
            <CustomModal title='Settings' isOpen={isOpen} isValid={formik.isValid} setIsOpen={setIsOpen} onConfirm={formik.handleSubmit}>
              <SettingForm formRef={formik} />
            </CustomModal>
          </AuthGuard>
          <SearchBar
            pageKey={SearchBarFilterTypes.SETTINGS}
            onSearch={(filters: any) => {
              setParamFilter({ filters: filters });
              Storage.setSearchBarFiltering(SearchBarFilterTypes.SETTINGS, { isMoreFilter: true, filters: filters });
            }}
            columns={columns}
          />
          <Table
            {...table}
            showSearchBar={false}
            data={datas}
            showColumnToggler={false}
            showPagination={true}
            showAction={false}
            getCoreRowModel={getCoreRowModel()}
          />
        </TabPanel>
        <TabPanel value='2' style={{ display: 'contents' }}>
          <DeviceTypeSettings />
        </TabPanel>
        <TabPanel value='3' style={{ display: 'contents' }}>
          <CompanySettings />
        </TabPanel>
      </TabContext>
    </>
  );
}
